import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Phone, Mail, Building2, Star } from 'lucide-react';

const Subcontractors = ({ user }) => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', name_arabic: '', cr_number: '', vat_number: '',
    phone: '', email: '', address: '', contact_person: '',
    specialization: '', iban: '', bank_name: '', rating: 0, notes: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/subcontractors');
      setList(r.data || []);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const reset = () => {
    setForm({
      name: '', name_arabic: '', cr_number: '', vat_number: '',
      phone: '', email: '', address: '', contact_person: '',
      specialization: '', iban: '', bank_name: '', rating: 0, notes: '',
    });
    setEditing(null);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error('Name is required');
    try {
      if (editing) {
        await axiosInstance.patch(`/subcontractors/${editing.id}`, form);
        toast.success('Updated');
      } else {
        await axiosInstance.post('/subcontractors', form);
        toast.success('Created');
      }
      setShowForm(false); reset(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this subcontractor?')) return;
    try {
      await axiosInstance.delete(`/subcontractors/${id}`);
      toast.success('Deleted'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const edit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || '', name_arabic: s.name_arabic || '', cr_number: s.cr_number || '',
      vat_number: s.vat_number || '', phone: s.phone || '', email: s.email || '',
      address: s.address || '', contact_person: s.contact_person || '',
      specialization: s.specialization || '', iban: s.iban || '', bank_name: s.bank_name || '',
      rating: s.rating || 0, notes: s.notes || '',
    });
    setShowForm(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('subcontractors.title')}</h1>
        <button
          data-testid="add-subcontractor-btn"
          onClick={() => { reset(); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold shadow"
        >
          <Plus size={18} /> {t('subcontractors.add')}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">{t('common.loading')}</div>
        ) : list.length === 0 ? (
          <div className="p-10 text-center text-gray-400">{t('subcontractors.empty')}</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr className="text-sm text-gray-700">
                <th className="px-4 py-3 text-left">{t('subcontractors.fields.code')}</th>
                <th className="px-4 py-3 text-left">{t('subcontractors.fields.name')}</th>
                <th className="px-4 py-3 text-left">{t('subcontractors.fields.specialization')}</th>
                <th className="px-4 py-3 text-left">{t('subcontractors.fields.contact')}</th>
                <th className="px-4 py-3 text-left">{t('subcontractors.fields.contracts')}</th>
                <th className="px-4 py-3 text-left">{t('subcontractors.fields.outstanding')}</th>
                <th className="px-4 py-3 text-left">{t('subcontractors.fields.retention')}</th>
                <th className="px-4 py-3 text-left">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id} className="border-b hover:bg-gray-50 text-sm">
                  <td className="px-4 py-3 font-mono text-xs">{s.code}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{s.name}</div>
                    {s.name_arabic && <div className="text-xs text-gray-500">{s.name_arabic}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{s.specialization || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {s.phone && <div className="flex items-center gap-1 text-xs"><Phone size={12} />{s.phone}</div>}
                    {s.email && <div className="flex items-center gap-1 text-xs"><Mail size={12} />{s.email}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono">{(s.total_contracts_value || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 font-mono text-orange-700">{(s.outstanding_balance || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 font-mono text-blue-700">{(s.retention_held || 0).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => edit(s)} className="p-1 text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-sub-${s.id}`}>
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => del(s.id)} className="p-1 text-red-600 hover:bg-red-50 rounded ml-1" data-testid={`delete-sub-${s.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold flex items-center gap-2"><Building2 size={20} /> {editing ? t('subcontractors.edit_title') : t('subcontractors.add_title')}</h2>
            </div>
            <form onSubmit={save} className="p-6 grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Name *</label>
                <input data-testid="sub-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Arabic Name</label>
                <input value={form.name_arabic} onChange={(e) => setForm({ ...form, name_arabic: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">CR Number</label>
                <input value={form.cr_number} onChange={(e) => setForm({ ...form, cr_number: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">VAT Number</label>
                <input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Specialization</label>
                <input value={form.specialization} onChange={(e) => setForm({ ...form, specialization: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" placeholder="e.g. Concrete works" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Contact Person</label>
                <input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Phone</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Bank</label>
                <input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">IBAN</label>
                <input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="w-full border rounded px-3 py-2 text-sm font-mono" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-600 mb-1 block">Address</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Rating (1-5)</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })}>
                      <Star size={20} className={n <= form.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-600 mb-1 block">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows="2" className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2 flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => { setShowForm(false); reset(); }} className="px-4 py-2 rounded-lg border text-sm">{t('common.cancel')}</button>
                <button type="submit" data-testid="save-sub-btn" className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold text-sm">{t('common.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subcontractors;
